import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { FaPlay, FaInstagram, FaClock, FaWhatsapp } from "react-icons/fa6";
import { Helmet } from "react-helmet";
import axios from 'axios';

export default function GalleryItem() {
    const { id } = useParams(); // Get the Instagram ID from the URL
    const { state } = useLocation(); // Get the state passed via Link
    const [item, setItem] = useState(state?.item || null); // Use state if available, otherwise null
    const [loading, setLoading] = useState(!state?.item); // Show loading only if state is not available
    const [error, setError] = useState(null);

    const apiMain = "https://bbyshin-backend.onrender.com/api"; // Main API configuration

  

    useEffect(() => {
        // Fetch the item data if it's not available in the state
        if (!item) {
            const fetchItem = async () => {
                try {
                    const response = await axios.get(`${apiMain}/instagram-feed/${id}`);
                    const fetchedData = response.data;

                    // Destructure the postData and article from fetchedData.article
                    const { postData } = fetchedData.article;

                    const combinedItem = {
                        ...postData,
                        ...fetchedData.article,
                    };
                    setItem(combinedItem); // Set the combined item
                    setLoading(false);
                } catch (err) {
                    console.error("Error fetching gallery item:", err);
                    if (err.response && err.response.status === 404) {
                        setError("Item not found");
                    } else {
                        setError("Failed to fetch item");
                    }
                    setLoading(false);
                }
            };

            fetchItem();
        }
    }, [id, item, apiMain]);

    if (loading) {
        return (
            <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen w-full flex items-center justify-center">
                <p>Loading...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen w-full flex items-center justify-center">
                <div className="text-center p-8 bg-white rounded-xl shadow-lg">
                    <h2 className="text-3xl font-[PoppinsB] text-gray-800 mb-4">Error</h2>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }
    if (!item) {
        return (
            <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen w-full flex items-center justify-center">
                <div className="text-center p-8 bg-white rounded-xl shadow-lg">
                    <h2 className="text-3xl font-[PoppinsB] text-gray-800 mb-4">No Item Found</h2>
                    <p className="text-gray-600">The requested gallery item could not be found.</p>
                </div>
            </div>
        );
    }
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": item.article.title,
        "description": item.article.meta?.description || "BBYSHIN towing service article",
        "author": {
            "@type": "Person",
            "name": "BBYSHIN",
        },
        "editor": {
            "@type": "Person",
            "name": "AI-Assisted Editorial Process",
        },
        "publisher": {
            "@type": "Organization",
            "name": "BBYSHIN",
            "logo": {
                "@type": "ImageObject",
                "url": "https://bbyshintowing.com/logo.png", // Replace with your actual logo URL
            },
        },
        "datePublished": item.timestamp,
        "image": item.media_type === "VIDEO" ? item.thumbnail_url : item.media_url,
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://bbyshintowing.com/gallery/${id}`, // Replace with your actual page URL
        },
    };
    return (
        <>
            <Helmet>
                <title>{item.article.title}</title>
                <meta name="description" content={item.article?.meta?.description} />
                <meta property="og:title" content={item.article?.meta?.title} />
                <meta property="og:description" content={item.article?.meta?.description} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:image" content={item.media_type === 'VIDEO' ? item.thumbnail_url : item.media_url} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={item.article?.meta?.title} />
                <meta name="twitter:description" content={item.article?.meta?.description} />
                <meta name="twitter:image" content={item.media_type === 'VIDEO' ? item.thumbnail_url : item.media_url} />
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>
            <main className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen w-full">
                <article
                    className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
                    itemScope
                    itemType="http://schema.org/Article"
                >
                    <header className="text-center mb-12">
                        <h1
                            className="text-4xl sm:text-5xl font-[PoppinsB] text-gray-800 tracking-wide mb-4"
                            itemProp="headline"
                        >
                            {item?.article.title}
                        </h1>
                        <meta itemProp="datePublished" content={item.timestamp} />
                        <div itemProp="author" itemScope itemType="http://schema.org/Person">
                            <meta itemProp="name" content="BBYSHIN" />
                            <meta itemProp="url" content="https://bbyshintowing.vercel.app" /> {/* Replace with the actual URL */}
                        </div>
                        <meta
                            itemProp="image"
                            content={item.media_type === "VIDEO" ? item.thumbnail_url : item.media_url}
                        />
                        <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
                            <span className="px-3 py-1 bg-[#fcfc00] bg-opacity-90 rounded-full font-medium">
                                {item.media_type}
                            </span>
                            <time className="flex items-center gap-2" dateTime={item.timestamp}>
                                <FaClock className="text-gray-500" />
                                {new Date(item.timestamp).toLocaleString()}
                            </time>
                        </div>
                    </header>

                    <figure className='mb-12 flex justify-center' itemProp="image">
                        {item.media_type === 'VIDEO' ? (
                            <a
                                href={item.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full max-w-2xl relative rounded-xl overflow-hidden shadow-xl"
                            >
                                <img
                                    src={item.thumbnail_url}
                                    alt={item.article.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                                    <FaPlay className='w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 text-white'></FaPlay>
                                </div>
                            </a>
                        ) : (
                            <div className='w-full max-w-2xl relative rounded-xl overflow-hidden shadow-xl'>
                                <img
                                    src={item.media_url}
                                    alt={item.article.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        )}
                    </figure>

                    <div className='prose prose-lg max-w-8xl mx-auto' itemProp="articleBody">
                        <section>
                            <p className="lead text-xl text-gray-700">
                                {item.article.intro}
                            </p>

                            <section>
                                <h2 className="text-3xl font-[PoppinsB] text-gray-800 mt-8 mb-4">Selalu Siap, Layanan 24 Jam</h2>
                                <p className="text-gray-700 text-xl">
                                    {item.article.selalu_siap}
                                </p>
                            </section>

                            <section>
                                <h2 className="text-3xl font-[PoppinsB] text-gray-800 mt-8 mb-4">Pelayanan Unggul dengan Harga Bersaing</h2>
                                <p className="text-gray-700 text-xl">
                                    {item.article.harga_berkompetitif}
                                </p>
                            </section>
                            <section>
                                <h2 className="text-3xl font-[PoppinsB] text-gray-800 mt-8 mb-4">Rangkaian Layanan Komprehensif BBYSHIN</h2>
                                <p className="text-gray-700 text-xl">
                                    BBYSHIN adalah solusi lengkap untuk berbagai kebutuhan transportasi dan pengangkutan. Layanan kami mencakup:
                                </p>
                                <ul className="list-disc pl-6 mt-4 space-y-2 text-xl">
                                    {item.article.layanan_komprehensif.map((layanan, index) => (
                                        <li key={index} className="text-gray-700">
                                            {layanan}
                                        </li>
                                    ))}
                                </ul>
                            </section>

                            <section className="bg-gray-50 rounded-xl shadow-lg p-6 sm:p-8 mt-8">
                                <h2 className="text-3xl font-[PoppinsB] text-gray-800 mb-6 flex items-center gap-3">
                                    <span className="w-2 h-8 bg-[#fcfc00] rounded-full"></span>
                                    Informasi Kontak
                                </h2>

                                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                    <p className="text-gray-700 text-lg">
                                        Saat situasi darurat terjadi, komunikasi yang cepat adalah kunci. Hubungi tim BBYSHIN melalui nomor berikut:
                                    </p>
                                </div>

                                <ul className="space-y-6">
                                    <li className="border-l-4 border-[#fcfc00] pl-4">
                                        <h3 className="text-xl font-[PoppinsB] text-gray-800 mb-4">Kontak Utama</h3>
                                        <div className="flex flex-wrap gap-4">
                                            <a
                                                href="tel:082288349889"
                                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#fcfc00] hover:bg-[#e6e600] text-gray-700 rounded-xl font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                            >
                                                <span className="text-2xl">📞</span>
                                                <span>0822 8834 9889</span>
                                            </a>
                                            <a
                                                href="https://wa.me/6282288349889"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#fcfc00] hover:bg-[#e6e600] text-gray-700 rounded-xl font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                            >
                                                <span className="text-2xl"><FaWhatsapp /></span>
                                                <span>WhatsApp</span>
                                            </a>
                                        </div>
                                    </li>

                                    <li className="border-l-4 border-[#fcfc00] pl-4">
                                        <h3 className="text-xl font-[PoppinsB] text-gray-700 mb-4">Kontak Alternatif</h3>
                                        <div className="flex flex-wrap gap-4">
                                            <a
                                                href="tel:08113309677"
                                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#fcfc00] hover:bg-[#e6e600] text-gray-700 rounded-xl font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                            >
                                                <span className="text-2xl">📞</span>
                                                <span>0811 3309 677</span>
                                            </a>
                                            <a
                                                href="https://wa.me/628113309677"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#fcfc00] hover:bg-[#e6e600] text-gray-700 rounded-xl font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                            >
                                                <span className="text-2xl"><FaWhatsapp /></span>
                                                <span>WhatsApp</span>
                                            </a>
                                        </div>
                                    </li>
                                </ul>
                            </section>

                            <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-gray-700 italic">
                                    Percayakan kebutuhan darurat kendaraan Anda kepada BBYSHIN untuk layanan yang responsif, andal, dan komprehensif. Misi kami adalah memastikan keselamatan dan ketenangan pikiran Anda di setiap perjalanan.
                                </p>
                            </div>
                        </section>

                        <div className='flex justify-center mt-8'>
                            <a
                                href={item.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#fcfc00] hover:bg-[#e6e600] text-gray-800 rounded-full font-medium transition-colors duration-200"
                                aria-label="View this post on Instagram"
                            >
                                <FaInstagram className="text-xl" />
                                View on Instagram
                            </a>
                        </div>
                        <p class="text-xs text-gray-400 mt-8">
                            This article was created using advanced writing tools and reviewed by a content editor for accuracy and relevance.
                        </p>
                    </div>
                </article>
            </main >
        </>
    );
}